import React, { useState, createContext, useContext, useCallback } from 'react';
import { X, CheckCircle2, AlertCircle, Info, AlertTriangle } from 'lucide-react';

type ToastType = 'success' | 'error' | 'info' | 'warning';

interface Toast {
  id: number;
  message: string;
  type: ToastType;
}

interface ToastContextType {
  toast: (message: string, type?: ToastType) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const ToastProvider = ({ children }: { children: React.ReactNode }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const removeToast = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const toast = useCallback((message: string, type: ToastType = 'info') => {
    const id = Date.now();
    setToasts((prev) => [...prev, { id, message, type }]);
    setTimeout(() => removeToast(id), 5000);
  }, [removeToast]);

  return (
    <ToastContext.Provider value={{ toast }}>
      {children}
      <div className="fixed bottom-4 right-4 z-[200] flex flex-col gap-2 w-full max-w-xs sm:max-w-sm">
        {toasts.map((t) => (
          <div 
            key={t.id}
            className={`
              flex items-center gap-3 p-4 rounded-2xl shadow-2xl border animate-in slide-in-from-right-full duration-300
              ${t.type === 'success' ? 'bg-[#8dc63f]/10 border-[#8dc63f]/20 text-[#76ab32]' : ''}
              ${t.type === 'error' ? 'bg-rose-50 border-rose-100 text-rose-800' : ''}
              ${t.type === 'warning' ? 'bg-amber-50 border-amber-100 text-amber-800' : ''}
              ${t.type === 'info' ? 'bg-blue-50 border-blue-100 text-blue-800' : ''}
            `}
          >
            {t.type === 'success' && <CheckCircle2 className="w-5 h-5 shrink-0 text-[#8dc63f]" />}
            {t.type === 'error' && <AlertCircle className="w-5 h-5 shrink-0 text-rose-500" />}
            {t.type === 'warning' && <AlertTriangle className="w-5 h-5 shrink-0 text-amber-500" />}
            {t.type === 'info' && <Info className="w-5 h-5 shrink-0 text-blue-500" />}
            
            <p className="flex-1 text-sm font-bold leading-tight">{t.message}</p>
            
            <button onClick={() => removeToast(t.id)} className="p-1 hover:bg-black/5 rounded-full transition-colors">
              <X className="w-4 h-4 opacity-50" />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToast must be used within ToastProvider');
  return context;
};



